import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchDailyQuizzes = createAsyncThunk(
  "dailyQuiz/fetchDailyQuizzes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/daily-quizzes", { params });
      // Return the data array directly
      return response.data.data || response.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch daily quizzes"
      );
    }
  }
);

export const fetchDailyQuizByDate = createAsyncThunk(
  "dailyQuiz/fetchDailyQuizByDate",
  async (date, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/daily-quizzes/${date}`, {
        params: { include_questions: true },
      });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch daily quiz"
      );
    }
  }
);

export const scheduleDailyQuiz = createAsyncThunk(
  "dailyQuiz/scheduleDailyQuiz",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/daily-quizzes", data);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to schedule daily quiz"
      );
    }
  }
);

export const updateDailyQuiz = createAsyncThunk(
  "dailyQuiz/updateDailyQuiz",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/daily-quizzes/${id}`, data);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update daily quiz"
      );
    }
  }
);

export const deleteDailyQuiz = createAsyncThunk(
  "dailyQuiz/deleteDailyQuiz",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/daily-quizzes/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete daily quiz"
      );
    }
  }
);

const dailyQuizSlice = createSlice({
  name: "dailyQuiz",
  initialState: {
    dailyQuizzes: [],
    currentDailyQuiz: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDailyQuiz: (state) => {
      state.currentDailyQuiz = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDailyQuizzes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyQuizzes.fulfilled, (state, action) => {
        state.loading = false;
        state.dailyQuizzes = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchDailyQuizzes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDailyQuizByDate.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchDailyQuizByDate.fulfilled, (state, action) => {
        state.currentDailyQuiz = action.payload;
      })
      .addCase(fetchDailyQuizByDate.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(scheduleDailyQuiz.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(scheduleDailyQuiz.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.dailyQuizzes)) {
          state.dailyQuizzes.unshift(action.payload);
        } else {
          state.dailyQuizzes = [action.payload];
        }
      })
      .addCase(scheduleDailyQuiz.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateDailyQuiz.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDailyQuiz.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.dailyQuizzes)) {
          const index = state.dailyQuizzes.findIndex(
            (quiz) => quiz.id === action.payload.id
          );
          if (index !== -1) {
            state.dailyQuizzes[index] = action.payload;
          }
        }
        state.currentDailyQuiz = action.payload;
      })
      .addCase(updateDailyQuiz.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteDailyQuiz.fulfilled, (state, action) => {
        if (Array.isArray(state.dailyQuizzes)) {
          state.dailyQuizzes = state.dailyQuizzes.filter(
            (quiz) => quiz.id !== action.payload
          );
        }
      });
  },
});

export const { clearError, clearCurrentDailyQuiz } = dailyQuizSlice.actions;
export default dailyQuizSlice.reducer;
